// 분노 유형 캐노니컬 메타 (디자인 핸드오프: TYPES 컬러 + TYPEL10N 이름·멘트).
// 결과 화면 1개당 팝 컬러는 정확히 1색(점수·유형명·곡선·CTA·카드 테두리).
// 유형명은 언어별 독립 작명(번역 아님). verdicts는 mock 헤드라인 풀(실제는 AI 런타임).

import type { Archetype, Lang } from "./types";

export interface ArchetypeMeta {
  color: string; // 팝 컬러
  name: Record<Lang, string>;
  verdicts: Record<Lang, string[]>;
}

export const ARCHETYPE_META: Record<Archetype, ArchetypeMeta> = {
  cooler: {
    color: "#3FD8F0", // Ice Cyan
    name: { ko: "냉각형", en: "The Off Switch", ja: "瞬間冷却タイプ" },
    verdicts: {
      ko: [
        "확 끓었다가 알아서 식었네요. 라면이었으면 완벽한 타이밍.",
        "순식간에 끓고 순식간에 미지근.",
        "터뜨려놓고 알아서 식혀버렸어요.",
      ],
      en: [
        "boiled over, then made yourself tea.",
        "full boil to room temp in record time.",
        "erupted, then quietly hit refrigerate.",
      ],
      ja: [
        "沸騰してから、自分で冷ましましたね。",
        "一気に沸いて、一気に常温。",
        "噴いたあと、しれっと冷蔵庫行き。",
      ],
    },
  },
  firewall: {
    color: "#FF9F1C", // Amber
    name: { ko: "방화벽형", en: "The Poker Face", ja: "ポーカーフェイスタイプ" },
    verdicts: {
      ko: [
        "할 말은 많은데 사회생활이 이겼네요.",
        "속은 활활, 내보낸 건 골라서.",
        "임시저장함이 다 뒤집어썼어요.",
      ],
      en: [
        "typed it all, sent almost none.",
        "the drafts folder took the real hit.",
        "diplomatic outside, infernal within.",
      ],
      ja: [
        "言いたいことは山ほど、出したのは少々。",
        "下書きフォルダが本体ですね。",
        "顔は外交官、中は火事。",
      ],
    },
  },
  cleancut: {
    color: "#FFE500", // Electric Yellow
    name: { ko: "속전속결형", en: "One and Done", ja: "秒で決着タイプ" },
    verdicts: {
      ko: [
        "11초 컷. 분노도 패스트푸드로 드시는군요.",
        "한 방. 깔끔. 군말 없음.",
        "할 말만 하고 칼같이 퇴장.",
      ],
      en: [
        "in, out, devastating. eleven seconds flat.",
        "one swing. clean. no notes.",
        "said your piece and logged off.",
      ],
      ja: [
        "秒で完了。怒りもファストフード派。",
        "一振りで、きれいに終了。",
        "言うだけ言って即ログアウト。",
      ],
    },
  },
  slowburn: {
    color: "#FF3B2E", // Magma Red
    name: { ko: "활화산형", en: "Low and Slow", ja: "じわじわ着火タイプ" },
    verdicts: {
      ko: [
        "한참 뜸 들이다 한 방에 터뜨렸네요. 빌드업 장인.",
        "끓이고 끓이다 냄비째 엎었어요.",
        "참고 참다가, 분화.",
      ],
      en: [
        "a slow build to one glorious detonation.",
        "simmered, then dropped the whole pot.",
        "patience, patience, ERUPTION.",
      ],
      ja: [
        "じっくり溜めて、一気に噴火。",
        "煮込んで煮込んで、鍋ごと投下。",
        "我慢、我慢、からの大噴火。",
      ],
    },
  },
  deadcalm: {
    color: "#26E0B0", // Mint
    name: { ko: "무풍형", en: "Room Temperature", ja: "凪タイプ" },
    verdicts: {
      ko: [
        "게이지가 거의 안 움직였어요. 도 닦으셨거나, 이미 다 내려놨거나.",
        "잔잔함. 평정인지 무서운 건지.",
        "파동 0. 존경 아니면 걱정.",
      ],
      en: [
        "suspiciously calm. we're a little scared.",
        "flat line. either zen or plotting.",
        "not a single ripple. respect, or concern.",
      ],
      ja: ["ほぼ無風。悟りか、何か企ててます？", "平坦。禅か、伏線か。", "波ゼロ。尊敬か、心配か。"],
    },
  },
  afterburn: {
    color: "#FF3FA4", // Magenta
    name: { ko: "재생형", en: "Aftershock", ja: "あとから効くタイプ" },
    verdicts: {
      ko: [
        "혼자 불냈다가 혼자 껐네요. 자급자족 분노.",
        "뜨겁게 시작해서 스르륵 가라앉았어요.",
        "불 질러놓고 직접 진화 완료.",
      ],
      en: [
        "lit the fire and put it out yourself.",
        "hot open, soft landing. self-soothed.",
        "all that heat, exhaled into smoke.",
      ],
      ja: [
        "自分で点けて、自分で消火。完全自給自足。",
        "熱くスタート、しれっと鎮火。",
        "燃やした分、煙にして放出。",
      ],
    },
  },
};

export function archetypeColor(id: Archetype): string {
  return ARCHETYPE_META[id].color;
}

export function archetypeName(id: Archetype, lang: Lang): string {
  return ARCHETYPE_META[id].name[lang];
}

/** mock 헤드라인용 유형 멘트(variant 회전). 실제 출시는 AI 런타임 생성. */
export function typeVerdict(id: Archetype, lang: Lang, variant: number): string {
  const arr = ARCHETYPE_META[id].verdicts[lang];
  return arr[variant % arr.length];
}
